import { Component, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EventType, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AuthService } from '@twaice-fe/frontend/shared/services';
import { selectors } from '@twaice-fe/frontend/shared/store';
import {
  ACCOUNT_SETTINGS_ROUTE,
  ANALYTICS_UPSELL_ROUTE,
  DATA_EXPLORER_ROUTE,
  ENERGY_ALERTS_ROUTE,
  ENERGY_ASSET_DASHBOARD_ROUTE,
  ENERGY_BMS_ALERTS_ROUTE,
  ENERGY_EVENTS_ROUTE,
  ENERGY_HEALTH_ROUTE,
  ENERGY_OVERVIEW_ROUTE,
  ENERGY_PENALTY_RISK_ROUTE,
  ENERGY_PERFORMANCE_MANAGER_ROUTE,
  ENERGY_REPORTS_V2_ROUTE,
  ENERGY_SAFETY_ROUTE,
  ENERGY_STORAGE_HIERARCHY_ROUTE,
  ENERGY_STORAGE_OVERVIEW_ROUTE,
  ENERGY_WARRANTY_TRACKER_ROUTE,
  MOBILITY_BASE_ROUTE,
  MOBILITY_DEPRECATED_BASE_ROUTE,
  MODEL_LIBRARY_BASE_ROUTE,
  PRIVACY_POLICY_ROUTE,
  PROFESSIONAL_SERVICES_ROUTE,
  REPORTING_ROUTE,
  STORAGE_CONFIGURATION_ROUTE,
} from '@twaice-fe/shared/constants';
import { CustomerType } from '@twaice-fe/shared/models';
import { filter, switchMap } from 'rxjs';

const { configsSelectors } = selectors;
@Component({
  selector: 'twaice-fe-dashboard-layout',
  templateUrl: './dashboard-layout.component.html',
  styleUrls: ['./dashboard-layout.component.scss'],
  standalone: false,
})
export class DashboardLayoutComponent {
  showNavigationMenu = false;
  showGlobalNavigationMenu = false;
  isFleet = false;
  isPerformanceManagerSolution = false;
  isAssetDashboard = false;
  isSafetySolution = false;
  isWarrantyTracker = false;
  isReportsV2 = false;
  isProfessionalServices = false;
  isDataExplorerSolution = false;
  isHealth = false;
  isPenaltyRiskSolution = false;
  isStorageHierarchy = false;
  isAlertsSolution = false;
  isBMSAlertsSolution = false;
  isRuleConfigurationPage = false;
  isBMSConfigurationPage = false;
  isStorageOverviewSolution = false;
  isStorageConfiguration = false;
  isEventsSolution = false;

  private customerType: CustomerType;

  constructor(
    private destroy$: DestroyRef,
    protected store: Store,
    private authService: AuthService,
    private router: Router
  ) {
    this.router.events
      .pipe(
        filter((routerEvent) => routerEvent.type === EventType.NavigationEnd),
        switchMap(() => this.authService.isInternalUser()),
        takeUntilDestroyed(this.destroy$)
      )
      .subscribe((isInternalUser) => {
        this.isPerformanceManagerSolution = this.router.url.includes(ENERGY_PERFORMANCE_MANAGER_ROUTE);
        this.isDataExplorerSolution = this.router.url.includes(DATA_EXPLORER_ROUTE);
        this.isFleet = this.router.url.includes(MOBILITY_BASE_ROUTE) || this.router.url.includes(MOBILITY_DEPRECATED_BASE_ROUTE);
        this.isAssetDashboard = this.router.url.includes(ENERGY_ASSET_DASHBOARD_ROUTE);
        this.isSafetySolution = this.router.url.includes(ENERGY_SAFETY_ROUTE);
        this.isAlertsSolution = this.router.url.includes(ENERGY_ALERTS_ROUTE);
        this.isBMSAlertsSolution = this.router.url.includes(ENERGY_BMS_ALERTS_ROUTE);
        this.isRuleConfigurationPage =
          this.router.url.includes(ENERGY_ALERTS_ROUTE + '/configuration') ||
          this.router.url.includes(ENERGY_ALERTS_ROUTE + '/notification-configuration');
        this.isBMSConfigurationPage =
          this.router.url.includes(ENERGY_BMS_ALERTS_ROUTE + '/configuration') ||
          this.router.url.includes(ENERGY_BMS_ALERTS_ROUTE + '/notification-configuration');
        this.isWarrantyTracker = this.router.url.includes(ENERGY_WARRANTY_TRACKER_ROUTE);
        this.isPenaltyRiskSolution = this.router.url.includes(ENERGY_PENALTY_RISK_ROUTE);
        this.isReportsV2 = this.router.url.includes(ENERGY_REPORTS_V2_ROUTE);
        this.isProfessionalServices = this.router.url.includes(PROFESSIONAL_SERVICES_ROUTE);
        this.isHealth = this.router.url.includes(ENERGY_HEALTH_ROUTE);
        this.isStorageHierarchy = this.router.url.includes(ENERGY_STORAGE_HIERARCHY_ROUTE);
        this.isStorageOverviewSolution = this.router.url.includes(ENERGY_STORAGE_OVERVIEW_ROUTE);
        this.isStorageConfiguration = this.router.url.includes(STORAGE_CONFIGURATION_ROUTE);
        this.isEventsSolution = this.router.url.includes(ENERGY_EVENTS_ROUTE);
        this.showNavigationMenu = !this.isSolutionWithoutNavigationMenu();
        this.showGlobalNavigationMenu = this.isSolutionWithGlobalSystemScope();

        // for internal users in fleet we always show the navigation menut
        if (isInternalUser && this.router.url.includes(MOBILITY_BASE_ROUTE)) {
          this.showNavigationMenu = true;
        }
      });

    this.store
      .select(configsSelectors.getCustomerType)
      .pipe(takeUntilDestroyed())
      .subscribe((customerType) => (this.customerType = customerType));
  }

  private isSolutionWithGlobalSystemScope(): boolean {
    return (
      this.router.url.includes(REPORTING_ROUTE) ||
      this.router.url.includes(PROFESSIONAL_SERVICES_ROUTE) ||
      this.router.url.includes(MODEL_LIBRARY_BASE_ROUTE)
    );
  }

  private isSolutionWithoutNavigationMenu(): boolean {
    return (
      this.isSolutionWithGlobalSystemScope() ||
      this.router.url.includes(ENERGY_OVERVIEW_ROUTE) ||
      this.router.url.includes(ENERGY_EVENTS_ROUTE) ||
      this.router.url.includes(ANALYTICS_UPSELL_ROUTE) ||
      this.router.url.includes(MOBILITY_BASE_ROUTE) ||
      this.router.url.includes(MOBILITY_DEPRECATED_BASE_ROUTE) ||
      (this.router.url.includes(REPORTING_ROUTE) && this.customerType === CustomerType.MOBILITY) ||
      this.router.url.includes(PRIVACY_POLICY_ROUTE) ||
      this.router.url.includes(ACCOUNT_SETTINGS_ROUTE)
    );
  }
}
